// src/features/clinical/components/patient/PatientSearch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaUser, FaIdCard, FaPhone } from 'react-icons/fa';
import clinicalApi from '../../services/clinicalApi';

const PatientSearch = ({ onSelectPatient, selectedPatient }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchPatients = async () => {
      if (searchTerm.length < 2) {
        setPatients([]);
        return;
      }

      setIsLoading(true);
      try {
        // Use the search endpoint or filter from all patients
        const response = await clinicalApi.searchPatients(searchTerm);
        const filtered=response.data;
        // const filtered = response.data.filter(patient => 
        //   patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        //   patient.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        //   (patient.contact && patient.contact.includes(searchTerm))
        // );
        setPatients(filtered);
        setShowDropdown(true);
      } catch (error) {
        console.error('Failed to search patients:', error);
        setPatients([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

const handleSelectPatient = (patient) => {
  onSelectPatient(patient);
  setSearchTerm(patient.full_name || '');
  setPatients([]); // clear dropdown
  setShowDropdown(false);
};

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Search Patient <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          type="text"
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Search by name, ID, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
        />
        <FaSearch className="absolute left-3 top-3 text-gray-400" />
        {isLoading && (
          <div className="absolute right-3 top-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {showDropdown && patients.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {patients.map((patient) => (
            <div
              key={patient.id}
              className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
              onClick={() => handleSelectPatient(patient)}
            >
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <FaUser className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{patient.full_name}</div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <FaIdCard className="mr-1 h-3 w-3" />
                      {patient.patient_code || 'N/A'}
                    </span>
                    <span className="flex items-center">
                      <FaPhone className="mr-1 h-3 w-3" />
                      {patient.mobile || 'N/A'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Age: {calculateAge(patient.age)} • {patient.gender || 'N/A'} • Blood Group {patient.blood_group || 'Blood group N/A'}
                  </div>
                  {patient.allergies && patient.allergies.length > 0 && (
                    <div className="text-xs text-red-500 mt-1">
                      Allergies: {patient.allergies.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && searchTerm.length >= 2 && patients.length === 0 && !isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center">
          <p className="text-gray-500">No patients found</p>
          <button
            onClick={() => window.location.href = '/clinical/patients/register'}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Register new patient
          </button>
        </div>
      )}

      {selectedPatient && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-green-800">{selectedPatient.full_name}</p>
              <p className="text-sm text-green-600">
                ID: {selectedPatient.patient_code} • {calculateAge(selectedPatient.age)} years • {selectedPatient.gender}
              </p>
            </div>
            <button
              onClick={() => {
                onSelectPatient(null);
                setSearchTerm('');
              }}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Change
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientSearch;